import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Card, CardActionArea, CircularProgress, InputAdornment, Stack, TextField, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import _ from 'lodash';
import clientPortalServices from 'app/services/client-portal-services';
import ShipmentDetailDialog from './ShipmentDetailDialog';
import { headline, statusColor } from './shipmentText';

// Portal "Mis pedidos" del perfil Cliente: el estado de los pedidos de SU cliente, agrupados
// por lo que viaja junto (una entrega). El backend decide que se ve; aca solo se dibuja.
// Ver hermes-backend Api::V1::ClientPortalController.

const Thumbnails = ({ urls }) => {
    const shown = (urls || []).slice(0, 2);
    return (
        <Box sx={{
            width: { xs: 72, sm: 120 }, height: { xs: 72, sm: 120 }, flexShrink: 0, borderRadius: 2,
            bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, p: 1
        }}>
            {shown.length === 0 && <Inventory2OutlinedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />}
            {shown.map((url) => (
                <Box key={url} component="img" src={url} alt=""
                     sx={{ maxWidth: shown.length > 1 ? '45%' : '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1, bgcolor: 'background.paper' }} />
            ))}
        </Box>
    );
};

const itemsSummary = (card) => {
    const first = card.items?.[0]?.product;
    if (!first) return null;
    const rest = (card.items_count || 0) - 1;
    return rest > 0 ? `${first} y ${rest} producto${rest === 1 ? '' : 's'} más` : first;
};

const ShipmentCard = ({ card, onOpen }) => {
    const ordersLabel = (card.orders || []).map((o) => o.code).filter(Boolean).join(', ');

    return (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <CardActionArea onClick={() => onOpen(card)} sx={{ p: 2 }}>
                <Stack direction="row" spacing={2.5} alignItems="center">
                    <Thumbnails urls={card.thumbnails} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" sx={{ color: statusColor(card), fontWeight: 600 }}>
                            {card.status}
                        </Typography>
                        <Typography variant="h3" sx={{ my: 0.5, fontWeight: 600 }}>
                            {headline(card)}
                        </Typography>
                        {itemsSummary(card) &&
                            <Typography variant="body2" color="text.secondary" noWrap>{itemsSummary(card)}</Typography>}
                        {ordersLabel &&
                            <Typography variant="caption" color="text.secondary">
                                {card.orders.length > 1 ? 'Pedidos' : 'Pedido'} {ordersLabel}
                            </Typography>}
                    </Box>
                </Stack>
            </CardActionArea>
        </Card>
    );
};

const EmptyText = ({ children }) => (
    <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>{children}</Typography>
);

const ClientPortal = () => {
    const [query, setQuery] = useState('');
    const [data, setData] = useState(null);
    const [delivered, setDelivered] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [selected, setSelected] = useState(null);
    const requestId = useRef(0);

    const load = useCallback((q) => {
        const id = ++requestId.current;
        setLoading(true);
        clientPortalServices.getShipments({ q: q || undefined, page: 1 })
            .then((resp) => {
                if (id !== requestId.current) return;
                setData(resp);
                setDelivered(resp?.delivered || []);
                setPage(1);
            })
            .catch(() => {})
            .finally(() => { if (id === requestId.current) setLoading(false); });
    }, []);

    const debouncedLoad = useRef(_.debounce((q) => load(q), 400)).current;

    useEffect(() => { load(''); }, [load]);
    useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad]);

    const loadMore = () => {
        const next = page + 1;
        setLoadingMore(true);
        clientPortalServices.getShipments({ q: query || undefined, page: next })
            .then((resp) => {
                setDelivered((prev) => _.uniqBy([...prev, ...(resp?.delivered || [])], (c) => `${c.kind}-${c.id}`));
                setPage(next);
            })
            .catch(() => {})
            .finally(() => setLoadingMore(false));
    };

    const upcoming = data?.upcoming || [];
    const hasMore = data?.delivered_meta && page < data.delivered_meta.total_pages;

    return (
        <Box sx={{ maxWidth: 960 }}>
            <Typography variant="h1" sx={{ mb: 0.5 }}>Mis pedidos</Typography>
            {data?.customer?.name &&
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {data.customer.name}{data.company?.name ? ` · pedidos con ${data.company.name}` : ''}
                </Typography>}

            <TextField
                fullWidth
                placeholder="Busca por número de pedido o producto"
                value={query}
                onChange={(e) => { setQuery(e.target.value); debouncedLoad(e.target.value.trim()); }}
                sx={{ mb: 4, maxWidth: 640, '& .MuiOutlinedInput-root': { borderRadius: 6, bgcolor: 'background.paper' } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />

            {loading && !data ? (
                <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
                <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
                    <Typography variant="h2" sx={{ mb: 2 }}>Próximas entregas</Typography>
                    {upcoming.length === 0
                        ? <EmptyText>{query ? 'No hay pedidos en curso que coincidan con la búsqueda.' : 'No tienes pedidos en curso.'}</EmptyText>
                        : upcoming.map((card) => <ShipmentCard key={`${card.kind}-${card.id}`} card={card} onOpen={setSelected} />)}

                    <Typography variant="h2" sx={{ mt: 4, mb: 2 }}>Entregados</Typography>
                    {delivered.length === 0
                        ? <EmptyText>{query ? 'No hay entregas que coincidan con la búsqueda.' : 'Todavía no tienes pedidos entregados.'}</EmptyText>
                        : delivered.map((card) => <ShipmentCard key={`${card.kind}-${card.id}`} card={card} onOpen={setSelected} />)}
                    {hasMore &&
                        <Box sx={{ textAlign: 'center', mt: 1, mb: 4 }}>
                            <Button onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? 'Cargando…' : 'Ver más'}
                            </Button>
                        </Box>}
                </Box>
            )}

            <ShipmentDetailDialog
                card={selected}
                steps={data?.steps}
                onClose={() => setSelected(null)}
            />
        </Box>
    );
};

export default ClientPortal;
